import { useCallback, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'
import { ReportBuilderWizard } from './components/ReportBuilderWizard'
import { SavedConfigs } from './components/SavedConfigs'
import { useReportBuilderStore } from './stores/reportBuilderStore'

export function ReportBuilderPage() {
	const { isEnabled: isReportBuilderEnabled, isLoading: isFeatureFlagLoading } = useFeatureFlag('report-builder')
	const { initialize, isInitializing, error, clearError, resetConfig, forms, currentConfig } = useReportBuilderStore()
	const hasInitialized = useRef(false)

	const handleBeforeUnload = useCallback(
		(e: BeforeUnloadEvent) => {
			const hasName = currentConfig.name.trim().length > 0
			const hasForms = currentConfig.selectedFormIds.length > 0
			if (hasName || hasForms) {
				e.preventDefault()
			}
		},
		[currentConfig.name, currentConfig.selectedFormIds.length],
	)

	useEffect(() => {
		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload)
		}
	}, [handleBeforeUnload])

	useEffect(() => {
		return () => {
			resetConfig()
		}
	}, [resetConfig])

	useEffect(() => {
		if (isReportBuilderEnabled && !hasInitialized.current) {
			hasInitialized.current = true
			initialize().catch(() => {
				hasInitialized.current = false
			})
		}
	}, [initialize, isReportBuilderEnabled])

	const handleRetry = () => {
		hasInitialized.current = false
		clearError()
		initialize().catch(() => {
			hasInitialized.current = false
		})
	}

	if (isFeatureFlagLoading || isInitializing) {
		return (
			<main className='w-full p-6'>
				<div className='flex items-center justify-center h-64'>
					<div className='text-center'>
						<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4' />
						<p className='text-neutral-600'>Loading Report Builder...</p>
					</div>
				</div>
			</main>
		)
	}

	if (!isReportBuilderEnabled) {
		return <Navigate to='/reports/detailed-data-reports' replace />
	}

	if (error && forms.length === 0) {
		return (
			<main className='w-full p-6'>
				<div className='flex items-center justify-center h-64'>
					<div className='text-center'>
						<p className='text-red-600 mb-4'>{error}</p>
						<button
							type='button'
							onClick={handleRetry}
							className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium'>
							Retry
						</button>
					</div>
				</div>
			</main>
		)
	}

	return (
		<main className='w-full p-6'>
			<div className='mb-6 no-print'>
				<h1 className='text-2xl font-semibold text-neutral-900'>Report Builder</h1>
				<p className='text-neutral-600 mt-1'>Create custom reports by selecting forms, cycles, and grouping options</p>
			</div>

			{error && (
				<div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between'>
					<span className='text-red-700'>{error}</span>
					<button
						type='button'
						onClick={clearError}
						className='text-red-500 hover:text-red-700'
						aria-label='Dismiss error'>
						×
					</button>
				</div>
			)}

			<div className='grid grid-cols-1 lg:grid-cols-4 gap-6'>
				<div className='lg:col-span-3'>
					<ReportBuilderWizard />
				</div>
				<div className='lg:col-span-1'>
					<SavedConfigs />
				</div>
			</div>
		</main>
	)
}
